
import { Record, Union } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { list_type, int32_type, record_type, bigint_type, union_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { FastSimulation__ComponentOf_6546966D, StepIndex } from "../SimTypes.js";
import { item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { fromInt32, op_Addition, min, op_LeftShift, op_Subtraction, compare, fromZero, fromOne, equals, fromUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { ofArray, fold, map } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { ofList, add, tryFind, toList } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { wordAt, sparseUpTo as sparseUpTo_1, fixedOf } from "../RamStore.js";
import { Result_Map, FSharpResult$2 } from "../../fable_modules/fable-library-js.5.13.0/Result.js";
import { extractFastSimulationState } from "./FastExtract.js";
import { toList as toList_1 } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeBigInt } from "../../fable_modules/fable-library-js.5.13.0/Range.js";

/**
 * Whether the design read or overwrote this location at the clock being shown. A write lands one
 * clock after the address and write-enable that caused it; a read is the same clock for an
 * asynchronous memory and one after for a synchronous one.
 */
export class RamRowType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["RAMWritten", "RAMRead", "RAMNormal"];
    }
    static RAMWritten = new RamRowType(0, []);
    static RAMRead = new RamRowType(1, []);
    static RAMNormal = new RamRowType(2, []);
}

export function RamRowType_$reflection() {
    return union_type("RamView.RamRowType", [], RamRowType, () => [[], [], []]);
}

export class RamRow extends Record {
    constructor(Addr, Value, Row) {
        super();
        this.Addr = Addr;
        this.Value = Value;
        this.Row = Row;
    }
}

export function RamRow_$reflection() {
    return record_type("RamView.RamRow", [], RamRow, () => [["Addr", bigint_type], ["Value", bigint_type], ["Row", RamRowType_$reflection()]]);
}

/**
 * What a held set of rows answers: the clock, how many locations were worth listing rather than
 * windowing, and where the window starts. Everything that changes the rows.
 * 
 * The request that goes out and the table that reads the result are built from the same key, so
 * a table cannot ask for one thing, be sent it, and then look for another.
 */
export class RamKey extends Record {
    constructor(Cycle, SparseUpTo, Start) {
        super();
        this.Cycle = (Cycle | 0);
        this.SparseUpTo = (SparseUpTo | 0);
        this.Start = Start;
    }
}

export function RamKey_$reflection() {
    return record_type("RamView.RamKey", [], RamKey, () => [["Cycle", int32_type], ["SparseUpTo", int32_type], ["Start", bigint_type]]);
}

/**
 * Which of the two displays a table is showing, and its rows.
 */
export class RamView extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["RamSparse", "RamWindow"];
    }
}

export function RamView_$reflection() {
    return union_type("RamView.RamView", [], RamView, () => [[["Item", list_type(RamRow_$reflection())]], [["start", bigint_type], ["rows", list_type(RamRow_$reflection())]]]);
}

function readAndWritten(fs, fc, cycle) {
    let matchValue_1, matchValue_3, step_4, a_1, this$_2, this$_3, step_7, a_2, this$_4, this$_5;
    if ((cycle < 0) ? true : ((fs.ClockTick - cycle) >= fs.MaxArraySize)) {
        return [undefined, undefined];
    }
    else {
        let step;
        const maxArraySize = fs.MaxArraySize | 0;
        const numStep = cycle | 0;
        const simStep = (numStep % maxArraySize) | 0;
        step = (new StepIndex(numStep, simStep, (simStep === 0) ? (maxArraySize - 1) : (simStep - 1)));
        const addressAt = (s) => {
            let this$_1;
            const step_1 = s | 0;
            const a = item(0, fc.InputLinks);
            if (a.Width > 32) {
                const this$ = a;
                return this$.BigIntSlab[(this$.StepBase + step_1)];
            }
            else {
                return fromUInt32((this$_1 = a, this$_1.UInt32Slab[(this$_1.StepBase + step_1)]));
            }
        };
        let readAt;
        const matchValue = fc.FType;
        switch (matchValue.tag) {
            case 39:
            case 42: {
                readAt = step.SimStep;
                break;
            }
            case 40:
            case 41: {
                readAt = step.SimStepOld;
                break;
            }
            default:
                readAt = toFail(`What? ${fc.FullName} should be a memory component`);
        }
        return [(matchValue_1 = fc.FType, (cycle === 0) ? ((matchValue_1.tag === 40) ? undefined : ((matchValue_1.tag === 41) ? undefined : addressAt(readAt))) : addressAt(readAt)), (matchValue_3 = fc.FType, (matchValue_3.tag === 40) ? undefined : ((matchValue_3.tag === 39) ? undefined : ((matchValue_3.tag === 41) ? ((cycle === 0) ? undefined : (equals((step_4 = (step.SimStepOld | 0), (a_1 = item(2, fc.InputLinks), (a_1.Width > 32) ? ((this$_2 = a_1, this$_2.BigIntSlab[(this$_2.StepBase + step_4)])) : fromUInt32((this$_3 = a_1, this$_3.UInt32Slab[(this$_3.StepBase + step_4)])))), fromOne()) ? addressAt(step.SimStepOld) : undefined)) : ((matchValue_3.tag === 42) ? ((cycle === 0) ? undefined : (equals((step_7 = (step.SimStepOld | 0), (a_2 = item(2, fc.InputLinks), (a_2.Width > 32) ? ((this$_4 = a_2, this$_4.BigIntSlab[(this$_4.StepBase + step_7)])) : fromUInt32((this$_5 = a_2, this$_5.UInt32Slab[(this$_5.StepBase + step_7)])))), fromOne()) ? addressAt(step.SimStepOld) : undefined)) : ((cycle === 0) ? undefined : undefined)))))];
    }
}

function markReadWrite(fs, fc, cycle, rows) {
    const patternInput = readAndWritten(fs, fc, cycle);
    return map((tupledArg_2) => {
        const _arg = tupledArg_2[1];
        return new RamRow(tupledArg_2[0], _arg[0], _arg[1]);
    }, toList(fold((acc, tupledArg_1) => {
        const addr = tupledArg_1[0];
        const rowType = tupledArg_1[1];
        if (addr != null) {
            const a_1 = addr;
            const matchValue = tryFind(a_1, acc);
            if (matchValue == null) {
                return add(a_1, [fromZero(), rowType], acc);
            }
            else {
                return add(a_1, [matchValue[0], rowType], acc);
            }
        }
        else {
            return acc;
        }
    }, ofList(map((tupledArg) => [tupledArg[0], [tupledArg[1], RamRowType.RAMNormal]], rows), {
        Compare: (x, y) => (compare(x, y) | 0),
    }), ofArray([[patternInput[0], RamRowType.RAMRead], [patternInput[1], RamRowType.RAMWritten]]))));
}

function storeOf(fs, fid_, fid__1, fc, cycle) {
    const fid = [fid_, fid__1];
    const matchValue = fc.FType;
    let matchResult, mem;
    switch (matchValue.tag) {
        case 41:
        case 42: {
            matchResult = 1;
            break;
        }
        case 40: {
            matchResult = 0;
            mem = matchValue.fields[0];
            break;
        }
        case 39: {
            matchResult = 0;
            mem = matchValue.fields[0];
            break;
        }
        default:
            matchResult = 2;
    }
    switch (matchResult) {
        case 0:
            return new FSharpResult$2(/* Ok */ 0, [fixedOf(mem)]);
        case 1: {
            const matchValue_1 = extractFastSimulationState(fs, cycle, fid[0], fid[1]);
            if (matchValue_1.tag === 3) {
                return new FSharpResult$2(/* Ok */ 0, [matchValue_1.fields[0]]);
            }
            else {
                return new FSharpResult$2(/* Error */ 1, [`unexpected state ${matchValue_1} in a memory at cycle ${cycle}`]);
            }
        }
        default:
            return new FSharpResult$2(/* Error */ 1, [`component is not a memory but a ${matchValue}`]);
    }
}

/**
 * The rows a RAM table shows at one clock.
 * 
 * `sparseUpTo` is the most non-zero locations worth listing: below it every one of them is
 * returned, above it a window of `rows` locations from `start` instead. A caller that wants a
 * window whatever the memory holds asks for `sparseUpTo = 0`.
 * 
 * Which of the two comes back is the implementation's to decide, not the caller's, because only
 * it knows how much the memory holds - and knowing that is the walk being decided about.
 * `RamStore.sparseUpTo` is bounded whatever the answer; see docs/dev/ramOverTheWire.md.
 */
export function ofFastSim(fs, fid_, fid__1, cycle, sparseUpTo, start, rows) {
    const fid = [fid_, fid__1];
    const matchValue = FastSimulation__ComponentOf_6546966D(fs, fid);
    if (matchValue != null) {
        const fc = matchValue;
        return Result_Map((ram) => {
            const lastLocation = op_Subtraction(op_LeftShift(fromOne(), ram.AddressWidth), fromOne());
            const matchValue_1 = sparseUpTo_1(ram, cycle, sparseUpTo);
            if (matchValue_1 == null) {
                return new RamView(/* RamWindow */ 1, [start, markReadWrite(fs, fc, cycle, map((addr) => [addr, wordAt(ram, cycle, addr)], toList_1(rangeBigInt(start, 1n, min(lastLocation, op_Subtraction(op_Addition(start, fromInt32(rows)), fromOne()))))))]);
            }
            else {
                return new RamView(/* RamSparse */ 0, [markReadWrite(fs, fc, cycle, matchValue_1)]);
            }
        }, storeOf(fs, fid[0], fid[1], fc, cycle));
    }
    else {
        return new FSharpResult$2(/* Error */ 1, ["no such component in this simulation"]);
    }
}

